import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { ArrowRight, CheckCircle2, Phone, Mail, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { 
  companyInfo, 
  services, 
  differentiation, 
  propertyTypes,
  serviceAreas 
} from '../mockData';

const NewHomePage = () => {
  const [formData, setFormData] = React.useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    propertyAddress: '',
    message: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    toast.success('Quote request submitted successfully!');
    setFormData({ name: '', company: '', phone: '', email: '', propertyAddress: '', message: '' });
  };

  return (
    <div className="pt-16">
      {/* HERO SECTION */}
      <section className="relative min-h-[700px] flex items-center overflow-hidden bg-navy-primary">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1580741990231-4aa1c1d9a76a" 
            alt="Commercial Property" 
            className="w-full h-full object-cover opacity-30"
          />
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 py-32">
          <div className="max-w-4xl">
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
              Your property is an asset.<br />We maintain it that way.
            </h1>
            
            <p className="text-xl sm:text-2xl text-white/90 mb-12 leading-relaxed max-w-3xl">
              Commercial landscape and snow management across Toronto and the Greater Toronto Area.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-6">
              <Link to="/contact">
                <Button 
                  size="lg" 
                  className="bg-white text-navy-primary hover:bg-ice-blue transition-all duration-300 shadow-lg hover:shadow-xl text-lg px-8 py-6"
                >
                  Request a Quote
                  <ArrowRight className="ml-2" size={20} />
                </Button>
              </Link>
              
              <a href="tel:9058874303">
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white hover:text-navy-primary transition-all duration-300 text-lg px-8 py-6"
                >
                  <Phone className="mr-2" size={20} />
                  (905) 887-4303
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* POSITIONING SECTION */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy-primary mb-8">
              Landscape management,<br />not just maintenance.
            </h2>
            <p className="text-xl text-gray-700 leading-relaxed">
              Illyria Landscape Management provides professional exterior property care for commercial sites. 
              We focus on consistency, presentation, and long-term site control across all seasons.
            </p>
          </div>
        </div>
      </section>

      {/* SERVICES SECTION */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 lg:gap-12">
            {services.map((service) => (
              <Card key={service.id} className="p-8 hover:shadow-xl transition-all duration-300 bg-white border-0">
                <div className="h-64 mb-6 overflow-hidden rounded-lg">
                  <img 
                    src={service.image} 
                    alt={service.title} 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-2xl font-bold text-navy-primary mb-4">
                  {service.title}
                </h3>
                <p className="text-gray-700 text-lg leading-relaxed">
                  {service.description}
                </p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* DIFFERENTIATION SECTION */}
      <section className="py-32 bg-navy-primary text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-4xl sm:text-5xl font-bold mb-16">
              We manage properties.<br />Not tasks.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {differentiation.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <CheckCircle2 className="text-ice-blue flex-shrink-0" size={28} />
                  <span className="text-xl">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROPERTY TYPES SECTION */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy-primary mb-16">
              Built for commercial properties
            </h2>
            <div className="space-y-6">
              {propertyTypes.map((type, index) => (
                <div key={index} className="flex items-center gap-4 text-xl text-gray-700">
                  <div className="w-2 h-2 bg-navy-primary rounded-full"></div>
                  {type}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SERVICE AREA SECTION */}
      <section className="py-32 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy-primary mb-8">
              Serving Toronto & the Greater Toronto Area (GTA)
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-12">
              {serviceAreas.map((area, index) => (
                <div key={index} className="text-lg text-gray-700">
                  {area}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT SECTION */}
      <section className="py-32 bg-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl sm:text-5xl font-bold text-navy-primary mb-16">
              Let's talk about your property
            </h2>
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
              {/* Contact Info */}
              <div className="lg:col-span-1 space-y-8">
                <div>
                  <h3 className="text-xl font-bold text-navy-primary mb-6">Contact</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-3">
                      <Phone className="text-navy-primary mt-1" size={20} />
                      <div>
                        <a href="tel:4169959273" className="text-gray-700 hover:text-navy-primary transition-colors">
                          {companyInfo.phone}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <Mail className="text-navy-primary mt-1" size={20} />
                      <div>
                        <a href={`mailto:${companyInfo.email}`} className="text-gray-700 hover:text-navy-primary transition-colors break-all">
                          {companyInfo.email}
                        </a>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin className="text-navy-primary mt-1" size={20} />
                      <div className="text-gray-700">
                        {companyInfo.address}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-2">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="company">Company</Label>
                      <Input
                        id="company"
                        name="company"
                        value={formData.company}
                        onChange={handleInputChange}
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="mt-2"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="propertyAddress">Property Address</Label>
                    <Input
                      id="propertyAddress"
                      name="propertyAddress"
                      value={formData.propertyAddress}
                      onChange={handleInputChange}
                      className="mt-2"
                    />
                  </div>

                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      className="mt-2 min-h-[120px]"
                    />
                  </div>

                  <Button 
                    type="submit" 
                    size="lg"
                    className="w-full bg-navy-primary hover:bg-navy-secondary text-white"
                  >
                    Submit
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewHomePage;
